import React from "react";
import useUpcomingInvoices from "./useUpcomingInvoices";
import moment from "moment";
import { NoDataInvoiceIcon } from "@/components/Icons";

const UpcomingInvoices: React.FC = () => {
  const { upcomingInvoices, isLoading, error } = useUpcomingInvoices();

  if (isLoading) {
    return (
      <div className="w-full max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-800 mb-5">
            # Upcoming Invoice (next 2 weeks)
          </h2>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-800 mb-5">
            # Upcoming Invoice (next 2 weeks)
          </h2>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">
              Error loading data: {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto">
      <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
        <h2 className="text-lg font-medium text-gray-800 mb-5">
          # Upcoming Invoice (next 2 weeks)
        </h2>

        <div className="h-96 flex flex-col">
          {upcomingInvoices.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-12">
              <NoDataInvoiceIcon className="w-24 h-24 mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-500 mb-2">
                No Upcoming Invoices
              </h3>
              <p className="text-sm text-gray-400 text-center max-w-xs">
                There are no upcoming invoices for the next 2 weeks.
              </p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-[#FFF5E9] sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left border-b-2 border-gray-200">
                      School Name
                    </th>
                    <th className="px-4 py-3 text-center border-b-2 border-gray-200">
                      Installment No.
                    </th>
                    <th className="px-4 py-3 text-center border-b-2 border-gray-200">
                      Expected Generation Date
                    </th>
                    <th className="px-4 py-3 text-center border-b-2 border-gray-200">
                      Installment Amount
                    </th>
                    <th className="px-4 py-3 text-center border-b-2 border-gray-200">
                      PDC Status
                    </th>
                    <th className="px-4 py-3 text-center border-b-2 border-gray-200">
                      Invoice Generation
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {upcomingInvoices.map((invoice, index) => (
                    <tr
                      key={index}
                      className={`border-b border-gray-100 hover:bg-gray-50 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      {/* School Name */}
                      <td className="px-4 py-3 text-orange-500 font-medium text-left">
                        {invoice.schoolName}
                      </td>

                      {/* Installment No */}
                      <td className="px-4 py-3 text-gray-600 text-center">
                        {invoice.installmentNumber ?? "—"}
                      </td>

                      {/* Expected Generation Date */}
                      <td className="px-4 py-3 text-gray-600 text-center">
                        {invoice.expectedInvoiceGenerationDate
                          ? moment(invoice.expectedInvoiceGenerationDate).format(
                              "DD-MMM-YYYY"
                            )
                          : "—"}
                      </td>

                      {/* Installment Amount */}
                      <td className="px-4 py-3 text-gray-700 text-center">
                        {invoice.installmentAmount
                          ? invoice.installmentAmount.toLocaleString("en-IN")
                          : "—"}
                      </td>

                      {/* PDC Status */}
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            invoice.pdcStatus
                              ? "text-green-700 bg-green-100"
                              : "text-red-700 bg-red-100"
                          }`}
                        >
                          {invoice.pdcStatus ? "Yes" : "No"}
                        </span>
                      </td>

                      {/* Invoice Generation */}
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            invoice.invoiceGeneration
                              ? "text-green-700 bg-green-100"
                              : "text-red-700 bg-red-100"
                          }`}
                        >
                          {invoice.invoiceGeneration ? "Yes" : "No"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingInvoices;
